import { isHotkey, type KeyboardEventLike } from 'is-hotkey'

import { isMac } from './plattform'

const HOTKEY_ACTIONS = [
    'enter',
    'space',
    'escape',
    'arrowUp',
    'arrowDown',
    'undo',
    'redo',
    'boostTerm',
    'toggleAutoscroll',
    'toggleEditingLock',
] as const
type HotkeyAction = (typeof HOTKEY_ACTIONS)[number]
type HotkeyDescriptorBase = {
    combination: string | string[] // is-hotkey format
    presentation: string // visible label
    ariaKeyshortcuts: string // aria-keyshortcuts value
}
type HotkeyDescriptor = HotkeyDescriptorBase & {
    check: (event: KeyboardEventLike) => boolean
}

const SHARED_HOTKEYS = {
    enter: {
        combination: 'enter',
        presentation: 'Enter',
        ariaKeyshortcuts: 'Enter',
    },
    space: {
        combination: 'space',
        presentation: 'Space',
        ariaKeyshortcuts: 'Space',
    },
    escape: {
        combination: 'esc',
        presentation: 'Esc',
        ariaKeyshortcuts: 'Escape',
    },
    arrowUp: {
        combination: 'ArrowUp',
        presentation: 'Arrow Up',
        ariaKeyshortcuts: 'ArrowUp',
    },
    arrowDown: {
        combination: 'ArrowDown',
        presentation: 'Arrow Down',
        ariaKeyshortcuts: 'ArrowDown',
    },
} satisfies Partial<Record<HotkeyAction, HotkeyDescriptorBase>>

export const HOTKEYS_WIN: Record<HotkeyAction, HotkeyDescriptorBase> = {
    ...SHARED_HOTKEYS,
    undo: {
        combination: 'ctrl+z',
        presentation: 'Ctrl+Z',
        ariaKeyshortcuts: 'Control+Z',
    },
    redo: {
        combination: 'ctrl+shift+z',
        presentation: 'Ctrl+Shift+Z',
        ariaKeyshortcuts: 'Control+Shift+Z',
    },
    boostTerm: {
        combination: 'alt+t',
        presentation: 'Alt+T',
        ariaKeyshortcuts: 'Alt+T',
    },
    toggleAutoscroll: {
        combination: 'ctrl+shift+p',
        presentation: 'Ctrl+Shift+P',
        ariaKeyshortcuts: 'Control+Shift+P',
    },
    toggleEditingLock: {
        combination: 'ctrl+e',
        presentation: 'Ctrl+E',
        ariaKeyshortcuts: 'Control+E',
    },
}

export const HOTKEYS_MAC: Record<HotkeyAction, HotkeyDescriptorBase> = {
    ...SHARED_HOTKEYS,
    undo: {
        combination: 'meta+z',
        presentation: '⌘Z',
        ariaKeyshortcuts: 'Meta+Z',
    },
    redo: {
        combination: 'meta+shift+z',
        presentation: '⌘⇧Z',
        ariaKeyshortcuts: 'Meta+Shift+Z',
    },
    boostTerm: {
        combination: 'alt+t',
        presentation: '⌥T',
        ariaKeyshortcuts: 'Alt+T',
    },
    toggleAutoscroll: {
        combination: 'meta+shift+p',
        presentation: '⌘⇧P',
        ariaKeyshortcuts: 'Meta+Shift+P',
    },
    toggleEditingLock: {
        combination: 'meta+e',
        presentation: '⌘E',
        ariaKeyshortcuts: 'Meta+E',
    },
}

const BASE_HOTKEYS = isMac ? HOTKEYS_MAC : HOTKEYS_WIN

export const HOTKEYS = HOTKEY_ACTIONS.reduce(
    (acc, action) => {
        acc[action] = {
            ...BASE_HOTKEYS[action],
            check: isHotkey(BASE_HOTKEYS[action].combination),
        }

        return acc
    },
    {} as Record<HotkeyAction, HotkeyDescriptor>,
)
